/**
 * product-reviews-widget.js
 *
 * Alpine data factory for the generic review card grid.
 * Consumed as x-data="reviewsWidget(token, domain)" in the Liquid snippet.
 *
 * Reviews were imported newest-first so the lowest metaobject IDs correspond
 * to the most recent entries. sortKey: "id" ascending returns the 3 most recent.
 */

const QUERY = /* GraphQL */ `
  query RecentReviews($first: Int!) {
    metaobjects(type: "reviews", first: $first, sortKey: "id") {
      nodes {
        fields {
          key
          value
        }
      }
    }
  }
`;

/** @param {string} isoDate @returns {string} */
const timeAgo = (isoDate) => {
  if (!isoDate) return '';
  const days = Math.floor((Date.now() - new Date(isoDate)) / 86_400_000);
  if (days < 1) return 'today';
  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'always' });
  for (const [limit, unit] of [
    [365, 'year'],
    [30, 'month'],
    [7, 'week'],
    [1, 'day'],
  ]) {
    if (days >= limit) return rtf.format(-Math.floor(days / limit), unit);
  }
};

/**
 * Alpine data factory.
 *
 * @param {string} token    Public Storefront API access token (read-only).
 * @param {string} domain   Shop permanent domain (*.myshopify.com).
 * @param {string} version  Storefront API version.
 */
window.reviewsWidget = (token, domain, version) => ({
  /** Placeholder objects keep the x-for loop populated during the API fetch.
   * Using index as :key means Alpine patches elements in-place — no DOM swap = no CLS.
   * @type {{ rating: number, stars: number[], title: string, text: string, meta: string }[]} */
  reviews: Array.from({ length: 3 }, () => ({ rating: 5, stars: [0, 1, 2, 3, 4], title: '', text: '', meta: '' })),
  loading: true,

  async init() {
    if (!token || !domain) {
      this.loading = false;
      return;
    }

    // Wait for x-for to render the placeholder articles, then observe the first one.
    // Articles have real dimensions; the display:contents wrapper does not.
    await this.$nextTick();
    const target = this.$el.querySelector('article') ?? this.$el.parentElement;

    const observer = new IntersectionObserver(
      (entries, obs) => {
        if (!entries[0].isIntersecting) return;
        obs.disconnect();
        this.fetchReviews();
      },
      { rootMargin: '200px' },
    );
    observer.observe(target);
  },

  async fetchReviews() {
    try {
      const res = await fetch(`https://${domain}/api/${version}/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({ query: QUERY, variables: { first: 3 } }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const { data, errors } = await res.json();
      if (errors?.length) throw new Error(errors[0].message);

      const nodes = data?.metaobjects?.nodes ?? [];

      this.reviews = nodes.map((node) => {
        const f = Object.fromEntries(node.fields.map(({ key, value }) => [key, value]));
        const rating = Math.round(parseFloat(f.rating) || 5);
        return {
          rating,
          stars: Array.from({ length: rating }, (_, i) => i),
          title: f.title || '',
          text: f.text || '',
          meta: [f.author_name, f.author_type, f.location, timeAgo(f.date)].filter(Boolean).join(' · '),
        };
      });
    } catch (err) {
      console.warn('[product-reviews-widget] Storefront API fetch failed:', err.message);
    } finally {
      this.loading = false;
    }
  },
});
